import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabase';

const AuthCtx = createContext(null);

// Session 8 (B3): agency client switcher persistence.
//   URL ?client=<id> is the source of truth, then localStorage, then Digital Lotus.
//   The client role ignores the param entirely.
const CLIENT_PARAM = 'client';
const STORAGE_KEY = 'lotus.agencyClient';
const DEFAULT_CLIENT = 'digitallotus';

function readStoredClient() {
  try { return window.localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
}
function writeStoredClient(id) {
  try { window.localStorage.setItem(STORAGE_KEY, id); } catch { /* private mode etc. */ }
}
function readUrlClient() {
  try { return new URLSearchParams(window.location.search).get(CLIENT_PARAM) || null; } catch { return null; }
}

// Pick the agency's active client on load: ?client= -> localStorage -> Digital Lotus.
function pickAgencyClient(cRows, meRow) {
  const has = (id) => !!id && cRows.some((c) => c.client_id === id);
  const fromUrl = readUrlClient();
  if (has(fromUrl)) return fromUrl;
  const fromStore = readStoredClient();
  if (has(fromStore)) return fromStore;
  if (has(DEFAULT_CLIENT)) return DEFAULT_CLIENT;
  if (has(meRow?.client_id)) return meRow.client_id;
  return cRows[0]?.client_id ?? null;
}

export function AuthProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [session,  setSession]  = useState(null);
  const [me,       setMe]       = useState(null);      // v_portal_me row (null if unknown user)
  const [clients,  setClients]  = useState([]);        // v_portal_clients rows (agency: many; client: 1)
  const [activeId, setActiveId] = useState(null);      // active client_id for agency view
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const lastSearchRef = useRef(null);                  // last location.search the sync effect saw
  // Session 11: true when the current page was reached WITHOUT ?client= and the
  // portal filled the active client in (localStorage / Digital Lotus / the
  // previous page). Slug pages use it to switch to the client that owns the
  // item (useSwitchToOwningClient). An explicit ?client= or a switcher pick
  // clears it.
  const [clientImplied, setClientImplied] = useState(false);
  const lastKeyRef  = useRef(null);                    // last location.key the sync effect saw
  // Session 14: agency Inbox scope. false = only the switcher's client (the
  // default), true = every client, alerts grouped by client. Shared with the
  // nav badge so both count the same rows. inboxTick bumps after a
  // mark-as-read so the badge re-counts. Read state itself lives server-side.
  const [inboxAll, setInboxAll] = useState(false);
  const [inboxTick, setInboxTick] = useState(0);
  const bumpInbox = useCallback(() => setInboxTick((n) => n + 1), []);
  const ownWriteRef = useRef(false);                   // the next location change is our own ?client= write

  // Load session on mount + subscribe to changes.
  useEffect(() => {
    let sub;
    (async () => {
      const { data: { session: s } } = await supabase.auth.getSession();
      setSession(s);
      const res = supabase.auth.onAuthStateChange((_evt, ns) => setSession(ns));
      sub = res.data.subscription;
    })();
    return () => { if (sub) sub.unsubscribe(); };
  }, []);

  // When the signed-in USER changes, reload me + clients. Session 55: keyed on
  // the user id, not the session object. supabase-js re-announces the session
  // (SIGNED_IN / TOKEN_REFRESHED) every time the tab regains focus; keying on
  // the object flipped `loading`, App showed "Loading…" and the whole route
  // tree unmounted, so any half-filled form (Add client, Welcome, a review)
  // was wiped when Leena switched tabs. A token refresh for the same user is
  // not a reason to reload anything; the client keeps its own token.
  const uid = session?.user?.id ?? null;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session) {
        setMe(null); setClients([]); setActiveId(null); setLoading(false);
        return;
      }
      setLoading(true); setError(null);
      const { data: meRow, error: meErr } = await supabase
        .from('v_portal_me').select('*').maybeSingle();
      if (cancelled) return;
      if (meErr) { setError(meErr.message); setLoading(false); return; }
      setMe(meRow || null);

      if (!meRow) { setClients([]); setActiveId(null); setLoading(false); return; }

      const { data: cRows, error: cErr } = await supabase
        .from('v_portal_clients').select('*').order('name');
      if (cancelled) return;
      if (cErr) { setError(cErr.message); setLoading(false); return; }
      setClients(cRows || []);
      // Agency: URL param -> localStorage -> Digital Lotus. Client: their one row.
      const preferredId = meRow.role === 'agency'
        ? pickAgencyClient(cRows || [], meRow)
        : (cRows?.[0]?.client_id ?? null);
      setActiveId(preferredId);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAgency = me?.role === 'agency';

  // Session 8 (B3): keep ?client= and localStorage in step with activeId.
  //   * The URL changed (deep link, in-app link carrying ?client=): the URL wins.
  //     A valid id switches the active client; an unknown or archived id falls
  //     back to Digital Lotus and the param is dropped. No error toast.
  //   * Otherwise (switcher change, navigation without the param): the active
  //     client is written back to the URL with replaceState semantics, so
  //     in-app links keep the client and history does not stack.
  useEffect(() => {
    if (!isAgency || clients.length === 0) return;
    if (location.pathname === '/reset' || location.pathname === '/set-password') return; // auth pages carry their own params
    const sp = new URLSearchParams(location.search);
    const param = sp.get(CLIENT_PARAM);
    const searchChanged = lastSearchRef.current !== location.search;
    lastSearchRef.current = location.search;
    const isValid = (id) => !!id && clients.some((c) => c.client_id === id);

    // A new location that is not our own replace: did it name its client?
    if (lastKeyRef.current !== location.key) {
      lastKeyRef.current = location.key;
      if (ownWriteRef.current) ownWriteRef.current = false;
      else setClientImplied(!isValid(param));
    }

    if (searchChanged && param && param !== activeId) {
      if (isValid(param)) { setActiveId(param); return; }
      // Unknown / archived id: fall back and drop the param.
      const fallback = isValid(DEFAULT_CLIENT) ? DEFAULT_CLIENT : (clients[0]?.client_id ?? null);
      sp.delete(CLIENT_PARAM);
      const search = sp.toString();
      setActiveId(fallback);
      ownWriteRef.current = true;
      navigate({ pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash }, { replace: true });
      return;
    }
    if (!activeId) return;
    if (param !== activeId) {
      sp.set(CLIENT_PARAM, activeId);
      ownWriteRef.current = true;
      navigate({ pathname: location.pathname, search: `?${sp.toString()}`, hash: location.hash }, { replace: true });
    }
    writeStoredClient(activeId);
  }, [isAgency, clients, activeId, location.pathname, location.search, location.hash, location.key, navigate]);

  // Anything outside the sync effect that picks a client (the switcher, "Add
  // new client", a page switching to an item's owner) is a deliberate choice.
  const chooseClient = useCallback((id) => {
    setClientImplied(false);
    setActiveId(id);
  }, []);

  // Session 5: re-read v_portal_clients (e.g. after "Add new client") and
  // optionally switch to a given client.
  const reloadClients = useCallback(async (selectId) => {
    const { data: cRows, error: cErr } = await supabase
      .from('v_portal_clients').select('*').order('name');
    if (cErr) throw cErr;
    setClients(cRows || []);
    if (selectId) chooseClient(selectId);
    return cRows || [];
  }, [chooseClient]);

  const activeClient = useMemo(
    () => clients.find((c) => c.client_id === activeId) || null,
    [clients, activeId]
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setMe(null); setClients([]); setActiveId(null);
  }, []);

  const value = {
    session, me, clients, activeId, setActiveId: chooseClient, clientImplied, activeClient, reloadClients,
    loading, error, signOut,
    isAgency,
    inboxAll, setInboxAll, inboxTick, bumpInbox,
    entitlements: activeClient?.entitlements || me?.entitlements || [],
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be inside <AuthProvider>');
  return ctx;
}
